import { Component, signal, inject, OnInit, AfterViewInit, computed, isDevMode } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '../layout.service';
import { MenuSection, MenuItem } from './menu-data';
import { MenuAccessService } from '../../core/permissions/menu-access.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  animations: [
    trigger('slideDown', [
      state('closed', style({ height: '0px', overflow: 'hidden', opacity: 0 })),
      state('open', style({ height: '*', overflow: 'hidden', opacity: 1 })),
      transition('closed => open', animate('280ms ease-out')),
      transition('open => closed', animate('550ms ease-in-out')),
    ]),
    trigger('panelSlide', [
      state('expanded', style({ opacity: 1, pointerEvents: 'auto' })),
      state('collapsed', style({ opacity: 0, pointerEvents: 'none' })),
      transition('expanded => collapsed', animate('180ms ease-in')),
      transition('collapsed => expanded', animate('220ms 130ms ease-out')),
    ])
  ]
})
export class SidebarComponent implements OnInit, AfterViewInit {
  layoutService = inject(LayoutService);
  private router  = inject(Router);
  private menuAccess = inject(MenuAccessService);

  isDev = isDevMode();

  /** ID de la sección de primer nivel activa (columna de íconos → columna de submenú) */
  activeMenuId = signal<string | null>(null);

  /** Menú filtrado según permisos del usuario */
  readonly filteredMenuSections = this.menuAccess.sections;

  /** Sección activa calculada para filtrar la segunda columna */
  activeMenu = computed(() =>
    this.filteredMenuSections().find(s => s.id === this.activeMenuId()),
  );

  /** IDs de los ítems de accordion abiertos (niveles 2 y 3) */
  openItemIds = signal(new Set<string>());

  ngOnInit(): void {
    // Set initial active section before first render to avoid collapsed→expanded flash
    this.activateMenuByRoute();
  }

  ngAfterViewInit() {
    if ((window as any).feather) {
      (window as any).feather.replace();
    }

    // Subscribe to route changes to keep the menu in sync after navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.activateMenuByRoute();
    });
  }

  private activateMenuByRoute() {
    let currentUrl = this.router.url;
    if (currentUrl.startsWith('/en-construccion')) {
      const urlTree = this.router.parseUrl(currentUrl);
      const fromUrl = urlTree.queryParams['from'];
      if (fromUrl) currentUrl = fromUrl;
    }

    for (const section of this.filteredMenuSections()) {
      if (this.isRouteInSection(section, currentUrl)) {
        this.activeMenuId.set(section.id);
        this.openRelatedItems(section.children, currentUrl);
        this.layoutService.sidebarCollapsed.set(false);
        return;
      }
    }
  }

  private isRouteInSection(section: MenuSection, url: string): boolean {
    return this.findInItems(section.children, url);
  }

  private findInItems(items: MenuItem[], url: string): boolean {
    for (const item of items) {
      if (item.route === url) return true;
      if (item.children && this.findInItems(item.children, url)) return true;
    }
    return false;
  }

  private openRelatedItems(items: MenuItem[], url: string) {
    for (const item of items) {
      if (item.children && this.findInItems(item.children, url)) {
        this.openItemIds.update(set => {
          const next = new Set(set);
          next.add(item.id);
          return next;
        });
        this.openRelatedItems(item.children, url);
      }
    }
  }

  /** Selecciona una sección de primer nivel y expande el panel */
  selectSection(id: string, event: Event): void {
    event.preventDefault();
    this.activeMenuId.set(id);
    this.layoutService.sidebarCollapsed.set(false);
  }

  /** Abre/cierra un ítem de accordion (niveles 2+) */
  toggleItem(id: string, event: Event): void {
    event.preventDefault();
    this.openItemIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) {
        next.delete(id);
        this.closeChildren(id, next);
      } else {
        // Cerrar hermanos del mismo nivel antes de abrir
        this.closeSiblings(id, next);
        next.add(id);
      }
      return next;
    });
  }

  /** Encuentra los hermanos del ítem y los cierra junto con sus descendientes */
  private closeSiblings(id: string, set: Set<string>): void {
    const activeSection = this.activeMenu();
    if (!activeSection) return;
    this.closeSiblingsInList(id, activeSection.children, set);
  }

  private closeSiblingsInList(id: string, items: MenuItem[], set: Set<string>): boolean {
    for (const item of items) {
      if (item.id === id) {
        // Encontrado: cerrar todos los hermanos (los demás ítems de esta lista)
        for (const sibling of items) {
          if (sibling.id !== id) {
            set.delete(sibling.id);
            if (sibling.children) this.closeItemList(sibling.children, set);
          }
        }
        return true;
      }
      if (item.children && this.closeSiblingsInList(id, item.children, set)) {
        return true;
      }
    }
    return false;
  }

  isOpen(id: string): boolean {
    return this.openItemIds().has(id);
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  /** Cierra recursivamente los hijos de un ítem cerrado */
  private closeChildren(parentId: string, set: Set<string>): void {
    const section = this.filteredMenuSections().find((s: MenuSection) => s.id === parentId);
    if (section) {
      this.closeItemList(section.children, set);
    } else {
      for (const s of this.filteredMenuSections()) {
        this.closeInChildren(parentId, s.children, set);
      }
    }
  }

  private closeInChildren(parentId: string, items: MenuItem[], set: Set<string>): void {
    for (const item of items) {
      if (item.id === parentId && item.children) {
        this.closeItemList(item.children, set);
        return;
      }
      if (item.children) {
        this.closeInChildren(parentId, item.children, set);
      }
    }
  }

  private closeItemList(items: MenuItem[], set: Set<string>): void {
    for (const item of items) {
      set.delete(item.id);
      if (item.children) this.closeItemList(item.children, set);
    }
  }
}
